import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Header, Column } from "@tanstack/react-table";
import { ArrowDownZa, ArrowDownUp, ArrowDownAz } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SortButtonProps<TData> {
  header: Header<TData, unknown>;
  className?: string;
  sortValue: string | boolean;
  forceSortedStyle: boolean;
  onSortChange: (
    column: Column<TData, unknown>,
    direction: "asc" | "desc" | false,
  ) => void;
}

const getSortValue = (sort: string | boolean) => {
  if (sort === false) {
    return "default";
  }
  return sort === "desc" ? "desc" : "asc";
};

export default function SortButton<TData>({
  header,
  className,
  sortValue: sort,
  forceSortedStyle,
  onSortChange,
}: SortButtonProps<TData>) {
  const { t } = useTranslation();

  if (!header.column.getCanSort()) {
    return null;
  }

  const sortValue = getSortValue(sort);

  const handleSortChange = (value: string) => {
    if (value === "default") {
      onSortChange(header.column, false);
    } else {
      onSortChange(header.column, value as "asc" | "desc");
    }
  };

  const defaultClassName = "hover:text-slate-900";
  const sortedClassName = "hover:text-blue-900";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "p-0 w-6 h-6 bg-transparent hover:bg-transparent",
            className,
            {
              [defaultClassName]: sort === false,
              [sortedClassName]: sort !== false || forceSortedStyle,
            },
          )}
          data-testid="sort-column-dropdown-trigger"
        >
          {sort === false && <ArrowDownUp className="w-4 h-4" />}
          {sort === "asc" && <ArrowDownAz className="w-4 h-4" />}
          {sort === "desc" && <ArrowDownZa className="w-4 h-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t("table.sortBy")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={sortValue}
          onValueChange={handleSortChange}
        >
          <DropdownMenuRadioItem
            value="default"
            data-testid="sort-column-default-option"
          >
            <ArrowDownUp className="w-4 h-4 mr-2" />
            {t("table.sortDefault")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            value="asc"
            data-testid="sort-column-asc-option"
          >
            <ArrowDownAz className="w-4 h-4 mr-2" />
            {t("table.sortAscending")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            value="desc"
            data-testid="sort-column-desc-option"
          >
            <ArrowDownZa className="w-4 h-4 mr-2" />
            {t("table.sortDescending")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
